/** *  @description
 * A lightweight, immutable, and fully type-safe Dependency Injection (DI) container for TypeScript.
 *
 * This module provides a robust framework for managing application dependencies using an immutable,
 * functional approach. After building and configuring a container, call the `.use()` method
 * to get a fully-typed, ergonomic version that allows for direct property access to resolve
 * dependencies (e.g., `container.use().myService`).
 *
 * @see {@link createContainer} to begin.
 *
 * @example
 * // 1. Create and configure the container
 * const $appContainer = createContainer('App')
 *   .provided('config', { host: 'localhost', port: 8080 }) // a constant value
 *   .fn('fetchData', () => fetch('/api/data'))  // a function
 *   .dependent('apiClient', ['config'], ({ config }) => new ApiClient(config))
 *   .nested('auth', createAuthContainer)  // a sub-container
 *   .nested('db', ['config'], createDbContainer);  // a sub-container with dependencies
 *
 * // 2. Get the ergonomic, proxied version for consumption
 * const app = $appContainer.use;
 *
 * // 3. Resolve dependencies with the ergonomic syntax
 * const client = app.apiClient;
 * const authService = app.auth.signIn;  // access nested container services
 * client.fetchData();
 *
 * // The resulting structure when accessed via .use:
 * // app = {
 * //   config: { host: 'localhost', port: 8080 },
 * //   fetchData: () => Promise<Response>,
 * //   apiClient: ApiClient instance,
 * //   auth: {
 * //     signIn: (email, password) => Promise<User>,
 * //     signOut: () => Promise<void>,
 * //     currentUser: User | null
 * //   },
 * //   db: {
 * //     query: (sql) => Promise<Result>,
 * //     transaction: (fn) => Promise<void>
 * //   }
 * // }
 *
 * @best-practices
 *
 * ### Build vs. Use
 * Treat the container object as a "builder". Chain calls to `.lazyValue()`, `.dependent()`, etc., to
 * configure it. Once fully configured, call `.use()` once to get a reference to the consumable,
 * proxied version. Pass this "use" object throughout your application.
 *
 * ### Modular Architecture (Module Factory Pattern)
 * Avoid creating a single, monolithic container. Instead, structure your application into logical
 * modules. Define each module as a function that accepts a container and returns an enhanced version.
 * This makes dependencies explicit, improves reusability, and simplifies testing.
 *
 * @example
 * ```typescript
 * // Module factory pattern
 * function createAuthModule(parent: Container<{config: Config}>) {
 *   return parent.scope('auth')
 *     .fn('signIn', async (email: string, password: string) => { ... })
 *     .fn('signOut', async () => { ... });
 * }
 *
 * // Compose modules hierarchically
 * const $base = createContainer('base')
 *   .provided('config', { apiUrl: 'https://api.example.com' })
 *   .nested('auth', createAuthModule)
 *   .nested('db', createDatabaseModule);
 *
 * // Access via .use for ergonomic API
 * const app = $base.use;
 * await app.auth.signIn('user@example.com', 'password');
 *
 * // Resulting tree structure:
 * // base
 * // ├── config: { apiUrl: '...' }
 * // ├── auth (nested container)
 * // │   ├── signIn: Function
 * // │   └── signOut: Function
 * // └── db (nested container)
 * //     ├── query: Function
 * //     └── transaction: Function
 * ```
 */

import ErrorStackParser from 'error-stack-parser';

import { assert, errorMessage, FatalError, getCaller } from '#errors.js';
import { simplify, stringify } from '#introspection.js';
import { quickHash } from '#utils.js';

type Simplify<T> = T; // { [KeyType in keyof T]: T[KeyType] } & {};

const LOG = '[DI]';

export type Providable = unknown;
export type ProvidedValues = Record<ProviderKey, unknown>;
export type LazyValueProvider<T extends Providable, Deps extends object = object> = ($container: Container<Deps>) => T;

export type Provider<T extends Providable = Providable> =
  | DependentProvider<T>
  | LazyValueProvider<T>
  | ConstantProvider<T>;

export class ConstantProvider<const T extends Providable> {
  constructor(readonly value: T) {}
}

export const runtime = { verbose: false };

const invalidNames = new Set([
  'valueOf',
  'toString',
  'constructor',
  'prototype',
  'length',
  'arguments',
  'caller',
  'name',
]);

function validateName(name: string): asserts name is ProviderKey & string {
  if (typeof name !== 'string') throw new DIError(`Dependency key must be a string, got: ${String(name)}`);
  if (invalidNames.has(name)) throw new DIError(`Invalid name: ${name}`);
}

const allowedProxyNames = ['container', 'prettyPrint', 'toString'];

/**
 * @internal
 * Represents a provider that has explicit dependencies on other providers.
 */
export class DependentProvider<const out T extends Providable> {
  constructor(
    readonly deps: ProviderKey[],
    readonly createValue: (deps: ProvidedValues, $container: Container<any>) => T,
  ) {}
}

/**
 * @internal
 * Represents a mapped type where each key in the given `Providing` type is transformed into a corresponding `Provider` of the same type.
 * This utility type is used to create a structure where each property in the original type is replaced by a `Provider` wrapping the property's type.
 * @template Provides The original record type where keys represent property names and values represent property types.
 */
export type ProvidersFor<T extends ProvidedValues> = { [K in keyof T]: Provider<T[K]> };
export type Providers = ProvidersFor<ProvidedValues>;

// /**
//  * @internal
//  * The single, reusable Proxy handler that provides ergonomic access to container dependencies without exposing
//  * the details of underlying container implementation.
//  */
// function createProxyHandler<const Providing extends object>($target: Container<Providing>): Providing {
//   return new Proxy({} as Providing, {
//     has(_target, p) {
//       // Let internal JS mechanisms and libraries work with their symbols.
//       if (typeof p !== 'string') return Reflect.has($target, p);
//       else if (allowedProxyNames.includes(p)) return true;
//       else return $target.has(p);
//     },
//     ownKeys() {
//       return [...Object.keys($target.getProviders()), ...allowedProxyNames];
//     },
//     getOwnPropertyDescriptor(_target, p) {
//       if (typeof p !== 'string') return;
//       if (allowedProxyNames.includes(p)) {
//         switch (p) {
//           case 'toString': {
//             return { value: () => $target.toProxyString(), configurable: true };
//           }
//           case 'prettyPrint': {
//             return { value: () => $target.prettyPrint(), configurable: true };
//           }
//           case 'container': {
//             return { value: $target, configurable: true };
//           }
//           default: {
//             return;
//           }
//         }
//       } else if ($target.has(p)) {
//         return { configurable: true, enumerable: true };
//       } else return;
//     },
//     get(_target, prop, receiver) {
//       if (prop === 'container') return $target;
//       // Forward symbol-based properties, which are used by libraries like MobX, Immer, etc.
//       // eslint-disable-next-line @typescript-eslint/no-unsafe-return
//       else if (typeof prop !== 'string') return Reflect.get($target, prop, receiver);
//       else if (!$target.has(prop)) return;
//       // log('info', `[${target.name}:proxy] Resolving dependency: ${prop}`);
//       else if (prop === '$self') return $target.resolve('$self' as keyof Providing);
//       else return $target.resolve(prop as keyof Providing);
//     },
//   });
// }

export type and<Key extends string, Value, O extends object> = Merge<O, { [k in Key]: Value }>;
export type Merge<A, B> = Simplify<A & B>;

interface DebugData {
  type?: string;
}
const debugData = new WeakMap<object, DebugData>();

function setDebugData(obj: object, data: DebugData) {
  debugData.set(obj, data);
}

function getDebugData(obj: object): DebugData {
  return debugData.get(obj) ?? {};
}

export type ProviderKey = string;

/**
 * Represents a proxy container that extends the provided values while maintaining
 * access to the original container instance. This allows using the dependencies provided
 * by the container in a simple way, like if it was a simple object.
 * and operations on the provided values while still referencing the originating container.
 *
 * @template Providing - A generic type extending ProvidedValues that represents the
 * set of values being proxied and resolved through the container.
 */

export type ProvidedDeps<
  AvailableDeps extends object = object,
  RequiredKeys extends Array<keyof AvailableDeps> = Array<keyof AvailableDeps>,
> = Pick<AvailableDeps, RequiredKeys[number]>;

/**
 * An immutable, type-safe dependency injection container.
 * Each registration method returns a new instance of the container.
 */
export class Container<Providing extends object> {
  /**
   * @internal
   * Users should use `createContainer()` instead of the constructor.
   */
  constructor(
    readonly name: string,
    private readonly providers: Record<string, Provider>,
    private readonly instances: Map<string, Providable>,
    private readonly parents: Array<Container<any>> = [],
  ) {}

  debugType?: string;

  /**
   * Returns a proxied version of the container that provides an ergonomic, fully type-safe API for resolving dependencies via direct property access.
   *
   * @example
   * ```ts
   *  const container = createContainer('App').value('apiClient', () => new ApiClient());
   *  const app = container.use;
   *  app.apiClient.fetchData();
   * ```
   * @returns The proxied, ergonomic container.
   */
  get use(): Providing {
    if (!this.#useInstance) {
      if (runtime.verbose) console.debug(`${LOG} [${this.name}] Creating proxy for container usage`);
      const resolved = {} as Providing;
      for (const key of Object.keys(this.getProviders())) {
        resolved[key as keyof Providing] = this.resolve(key as keyof Providing);
      }
      this.#useInstance = resolved;
    }
    return this.#useInstance;
  }

  get [Symbol.toStringTag]() {
    return `NamedContainer ${this.name}${this.parents.map((p) => ` ${p.name}`).join(' >')}`;
  }

  has(key: ProviderKey): boolean {
    if (key === '$self') return true;
    if (invalidNames.has(key)) return false;
    const providers = this.getProviders();
    return key in providers;
  }

  /**
   * Registers a function without dependencies in the container.
   *
   * @example
   * ```ts
   * const $base = createContainer('base')
   *   .provided('config', { apiUrl: 'https://api.example.com' });
   * ```
   `
   * @param key - The provider key that will be used to identify the function in the container.
   * @param fun - The function to be associated with the specified key.
   * @return A new container instance including the updated provisioning logic with the provided key and function.
   */
  fn<const Key extends ProviderKey, const FunValue extends (...args: any[]) => any>(
    key: Key,
    fun: FunValue,
  ): Container<and<Key, FunValue, Providing>>;

  /**
   * Registers a function with dependencies in the container.
   *
   * @example
   * ```ts
   * const $base = createContainer('base')
   *   .fn('apiClient', ['config'], (deps, $self) => new ApiClient(deps.config));
   * ```
   * @param key - The provider key that will be used to identify the function in the container.
   * @param deps - The dependencies required by the function.
   * @param createFun - The function to be associated with the specified key.
   * @return A new container instance including the updated provisioning logic with the provided key and function.
   */
  fn<
    const Key extends ProviderKey,
    const FunValue extends (...args: any[]) => any,
    const Requires extends Array<keyof Providing>,
  >(
    key: Key,
    deps: Requires,
    createFun: (deps: ProvidedDeps<Providing, Requires>, $container: Container<Providing>) => FunValue,
  ): Container<and<Key, FunValue, Providing>>;

  fn<
    const Key extends ProviderKey,
    const Requires extends Array<keyof Providing>,
    const FunValue extends (...args: any[]) => any,
  >(
    key: Key,
    deps: Requires | FunValue,
    createFun?: (deps: ProvidedDeps<Providing, Requires>, $container: Container<Providing>) => FunValue,
  ): Container<and<Key, FunValue, Providing>> {
    validateName(key);
    assert(deps !== undefined, `Function provider for "${key}" is undefined`);
    if (typeof deps === 'function') {
      const fun = deps;
      if (runtime.verbose) {
        console.debug(
          `${LOG} [${this.name}] Registering function "${key}" with dependencies: [${fun.name || 'lambda'}]`,
        );
      }
      return this.provided(key, fun);
    } else {
      if (runtime.verbose) {
        console.debug(`${LOG} [${this.name}] Registering function "${key}" with dependencies: [${deps.join(', ')}]`);
      }
      assert(createFun !== undefined, `Function provider for "${key}" is undefined`);
      return this.dependent(key, deps, (deps, $self) => createFun(deps, $self));
    }
  }

  /**
   * Registers a static value or a simple, zero-argument factory function.
   * @param key The unique key for the provider.
   * @param provider The value or factory function.
   * @returns A new container instance with the provider registered.
   */
  providedLazy<const Key extends ProviderKey, const Value extends Providable>(
    key: Key,
    provider: LazyValueProvider<Value, Providing>,
  ): Container<and<Key, Value, Providing>> {
    if (runtime.verbose) console.debug(`${LOG} [${this.name}] Registering value "${key}"`);
    validateName(key);
    assert(provider !== undefined, `Value provider for "${key}" is undefined`);
    setDebugData(provider, { type: `value: ${simplify(provider)}` });
    return new Container(this.name, { ...this.providers, [key]: provider as Provider }, this.instances, this.parents);
  }

  provided<const Key extends ProviderKey, const Value extends Providable>(
    key: Key,
    value: Value,
  ): Container<and<Key, Value, Providing>> {
    if (runtime.verbose) console.debug(`${LOG} [${this.name}] Registering provided value "${key}"`);
    validateName(key);
    assert(value !== undefined, `Provided value for "${key}" is undefined`);
    const provider = new ConstantProvider(value);
    setDebugData(provider, { type: `provided: ${simplify(value)}` });
    return new Container(this.name, { ...this.providers, [key]: provider } as const, this.instances, this.parents);
  }

  /**
   * Registers a provider that depends on other providers within the container.
   * @param key The unique key for the new provider.
   * @param deps An array of keys for the required dependencies.
   * @param createValue A function that receives an object of resolved dependencies and returns the new service instance.
   * @param debugData
   * @returns A new container instance with the provider registered.
   */
  dependent<
    const Key extends ProviderKey,
    const Value extends Providable,
    const Requires extends Array<keyof Providing>,
  >(
    key: Key,
    deps: Requires,
    createValue: (deps: ProvidedDeps<Providing, Requires>, $container: Container<Providing>) => Value,
    debugData?: DebugData,
  ): Container<and<Key, Value, Providing>> {
    if (runtime.verbose) {
      console.debug(`${LOG} [${this.name}] Registering dependent "${key}" with dependencies: [${deps.join(', ')}]`);
    }
    validateName(key);
    assert(deps !== undefined, `Dependency list for "${key}" is undefined`);
    assert(createValue !== undefined, `Dependent provider for "${key}" is undefined`);
    const provider = new DependentProvider(
      deps as string[],
      createValue as (deps: ProvidedValues, $container: Container<Providing>) => Value,
    );
    setDebugData(provider, { type: `dependent: ${createValue.name || 'lambda'}`, ...debugData });
    return new Container(this.name, { ...this.providers, [key]: provider as Provider }, this.instances, this.parents);
  }

  /**
   * Registers a nested container as a dependency, creating a modular, hierarchical structure.
   * The nested container has its own scope and can access the parent container.
   *
   * @example
   * ```typescript
   * // Without dependencies
   * const $app = $base.nested('auth', createAuthContainer);
   *
   * // With dependencies from parent
   * const $app = $base.nested('chats', ['db', 'flows'], (parent, { db, flows }) =>
   *   createChatsContainer(parent, { db, flows })
   * );
   *
   * // Access nested services
   * const app = $app.use;
   * app.auth.signIn(); // Access nested container's services
   * ```
   */
  nested<const Key extends ProviderKey, NestedProviding extends ProvidedValues>(
    key: Key,
    createContainer: ($container: Container<Providing>) => Container<NestedProviding>,
  ): Container<and<Key, NestedProviding, Providing>>;

  nested<
    const Key extends ProviderKey,
    const NestedProviding extends ProvidedValues,
    const Requires extends Array<keyof Providing>,
  >(
    key: Key,
    deps: Requires,
    createContainer: (
      deps: ProvidedDeps<Providing, Requires>,
      $container: Container<Providing>,
    ) => Container<NestedProviding>,
  ): Container<and<Key, NestedProviding, Providing>>;

  nested<
    const Key extends ProviderKey,
    const NestedProviding extends ProvidedValues,
    const Requires extends Array<keyof Providing>,
  >(
    key: Key,
    depsKeysOrCreateContainer: Requires | (($container: Container<Providing>) => Container<NestedProviding>),
    createContainer?: (
      deps: ProvidedDeps<Providing, Requires>,
      $container: Container<Providing>,
    ) => Container<NestedProviding>,
  ): Container<and<Key, NestedProviding, Providing>> {
    validateName(key);
    assert(depsKeysOrCreateContainer !== undefined, `Value for nested container for "${key}" is undefined`);
    if (typeof depsKeysOrCreateContainer === 'function') {
      return this.providedLazy(key, ($container) => depsKeysOrCreateContainer($container).use);
    } else {
      assert(createContainer !== undefined, `Container for "${key}" is undefined`);
      return this.dependent(key, depsKeysOrCreateContainer, (deps) => createContainer(deps, this).use);
    }
  }

  /**
   * Registers a provider that produces a function to be called later.
   * This is useful for defining side-effectful operations like initialization.
   * @param key The unique key for the new procedure.Registering provided value
   * @param deps An array of keys for the required dependencies.
   * @param body A function that receives resolved dependencies and returns the procedure's result.
   * @returns A new container instance with the procedure registered.
   */
  procedure<
    const Key extends ProviderKey,
    const Ret extends unknown | void,
    const Requires extends Array<keyof Providing>,
  >(
    key: Key,
    deps: Requires,
    body: (deps: ProvidedDeps<Providing, Requires>, $container: Container<Providing>) => Ret,
  ): Container<and<Key, () => Ret, Providing>> {
    validateName(key);
    assert(deps !== undefined, `Dependency list for "${key}" is undefined`);
    assert(body !== undefined, `Procedure for "${key}" is undefined`);
    return this.dependent(key, deps, (resolved) => () => body(resolved, this), {
      type: `procedure: ${body.name || 'lambda'}`,
    });
  }

  /**
   * Creates a new, scoped child container that inherits all providers from the parent.
   * Useful for creating logical groupings of services while maintaining access to parent dependencies.
   *
   * @param name The name for the new scope.
   * @returns A new child container instance.
   *
   * @example
   * ```typescript
   * const $base = createContainer('base')
   *   .provided('config', { apiUrl: 'https://api.example.com' });
   *
   * const $features = $base.scope('features')
   *   .nested('auth', createAuthContainer)
   *   .nested('chats', createChatsContainer);
   *
   * // The features scope can access parent's config
   * const features = $features.use;
   * console.log(features.config.apiUrl); // Available from parent
   * ```
   */
  scope(name: string): Container<Providing> {
    if (runtime.verbose) console.debug(`${LOG} [${this.name}] Creating scope "${name}"`);
    const container = new Container<Providing>(name, {}, new Map(this.instances.entries()), [
      this as Container<Providing>,
    ]);
    container.precomputeProviders();
    return container;
  }

  /**
   * Returns a map of all providers registered directly in this container instance.
   * @returns A precisely-typed record of provider keys to their provider definitions.
   */
  getProviders(): Providers {
    if (this.#providersCache) return this.#providersCache;
    if (this.parents.length === 0) return this.providers as Providers;
    // detect if we are overriding a parent provider
    const mergedProviders = {} as Record<string, any>;
    const allProviders = [
      ...this.parents.map((parent) => [parent.name, parent.getProviders()] as const),
      [this.name, this.providers as Providers] as const,
    ];
    for (const [name, providers] of allProviders) {
      for (const key in providers) {
        if (mergedProviders[key] && runtime.verbose)
          console.warn(`${LOG} Overriding provider "${key}" from "${name}" in "${this.name}".`);
        mergedProviders[key] = providers[key];
      }
    }
    this.#providersCache = mergedProviders as Providers;
    return this.#providersCache;
  }

  /**
   * Resolves a dependency for a given key, with circular dependency detection.
   * It manages the resolution chain's lifecycle, ensuring it's cleared after a top-level call.
   * This makes the resolution process safe from re-entrant calls from the proxy.
   */
  resolve<Key extends keyof Providing>(key: Key): Providing[Key] {
    if (typeof key !== 'string') {
      throw new ResolutionError(`Invalid key "${String(key)}" where string was expected`);
    }
    validateName(key);
    // If a resolution is already in progress, this is a nested call.
    // We simply continue with the existing chain.
    if (this.#resolutionChain.length > 0) {
      return this._internalResolve(key) as Providing[Key];
    }
    try {
      return this._internalResolve(key) as Providing[Key];
    } catch (err) {
      if (err instanceof DIError) throw err;
      throw new ResolutionError(`Error resolving provider "${key}".`, { cause: err });
    } finally {
      this.#resolutionChain = [];
    }
  }

  /**
   * Merges this container with another, creating a new container with providers from both.
   * This is a form of composition, creating a new, flat container without a parent-child relationship.
   * @param other The container to merge into this one.
   * @returns A new container instance with the combined providers.
   */
  merge<const OtherProviding extends ProvidedValues>(
    other: Container<OtherProviding>,
  ): Container<Merge<Providing, OtherProviding>> {
    assert(other !== undefined, `Other container is undefined`);
    const newName = `${this.name}+${other.name}` as const;
    if (runtime.verbose) {
      console.debug(`${LOG} Merging container "${this.name}" with "${other.name}" into "${newName}"`);
    }
    const container = new Container(newName, {}, new Map([...this.instances.entries(), ...other.instances.entries()]), [
      this,
      other,
    ] as Array<Container<any>>);
    container.getProviders(); // pre-compute the providers for the merged container
    return container as Container<Merge<Providing, OtherProviding>>;
  }

  /**
   * Prints a visual representation of all available providers and their dependencies to the console.
   * This method recursively traverses the parent hierarchy to build a complete tree.
   */
  /**
   * Prints a visual representation of all available providers and their dependencies to the console.
   * This method recursively traverses the parent hierarchy to build a complete tree.
   */
  prettyPrint(): void {
    const output: string[] = [];

    const printNodeContents = (node: Container<any>, prefix: string) => {
      const localProviders = Object.entries(node.providers);
      const parents = node.parents;
      const totalItems = localProviders.length + (parents.length > 0 ? 1 : 0);
      let itemCount = 0;

      for (const [key, provider] of localProviders) {
        itemCount++;
        const isLast = itemCount === totalItems;
        const connector = isLast ? '└─' : '├─';
        const depPrefixBase = prefix + (isLast ? '  ' : '│ ');

        const debugData = provider !== null && typeof provider === 'object' ? getDebugData(provider) : {};
        if (isDependentProvider(provider) && provider.deps.length > 0) {
          const type = debugData.type ?? 'dependent';
          output.push(`${prefix}${connector}🔧 ${key} (${type})`);
          for (const [depIndex, dep] of provider.deps.entries()) {
            const isLastDep = depIndex === provider.deps.length - 1;
            const depConnector = isLastDep ? '└─' : '├─';
            output.push(`${depPrefixBase}${depConnector}🔗 ${dep}`);
          }
        } else {
          const type = debugData.type ?? 'value/constant';
          output.push(`${prefix}${connector}🔧 ${key} (${type})`);
        }
      }

      if (parents.length > 0) {
        itemCount++;
        const isLast = itemCount === totalItems;
        const connector = isLast ? '└─' : '├─';
        output.push(`${prefix}${connector}▶︎ Parents`);

        const parentPrefix = prefix + (isLast ? '  ' : '│ ');
        for (const [index, parent] of parents.entries()) {
          buildTree(parent, parentPrefix, index === parents.length - 1);
        }
      }
    };

    const buildTree = (node: Container<any>, prefix: string, isTail: boolean) => {
      const connector = isTail ? '└─' : '├─';
      const debugData = getDebugData(node);
      const type = debugData.type ?? 'container';
      output.push(`${prefix}${connector}📦 ${node.name} (${type})`);
      printNodeContents(node, prefix + (isTail ? '  ' : '│ '));
    };

    output.push(`📦 ${this.name}`);
    printNodeContents(this, '');

    console.log(output.join('\n'));
  }

  /**
   * Prints a visual representation of all available providers and their dependencies to the console.
   */

  toProxyString(): string {
    return stringify(this.instances);
  }

  toString(): string {
    return `[NamedContainer ${this.name} ${this.parents.map((p) => p.name).join(' > ')}]`;
  }

  protected precomputeProviders() {
    this.getProviders();
  }

  /**
   * @internal
   * This is the "phantom" property used to infer the `Providing` type map.
   */
  #useInstance: Providing | undefined;
  #providersCache: Record<string, Provider> | undefined;
  /**
   * @internal
   * The active resolution chain for the current, top-level resolve operation.
   * This is managed as state on the instance to allow the .use proxy to participate
   * in cycle detection.
   */
  #resolutionChain: string[] = [];

  /**
   * @internal
   * The core implementation of the resolution logic.
   * It mutates the instance's #resolutionChain to track the dependency graph traversal.
   */
  private _internalResolve(key: string): Providable {
    validateName(key);

    // 1. Always check the instance cache first for immediate resolution.
    if (this.instances.has(key)) {
      return this.instances.get(key);
    }

    const chainKey = `${this.name}.${key}`;
    // 2. Check for circular dependencies using the shared chain.
    if (this.#resolutionChain.includes(chainKey)) {
      const cycle = [...this.#resolutionChain, chainKey].join(' -> ');
      if (runtime.verbose) console.error(`${LOG} [${this.name}] Circular dependency detected: ${cycle}`);
      throw new CircularDependencyError(`Circular dependency detected: ${cycle}`, { debugData: getDebugData(this) });
    }

    // Add the current resolution to the chain.
    this.#resolutionChain.push(chainKey);

    try {
      // 3. Check for a provider defined in the current scope.
      const valueProvider = this.providers[key];

      if (valueProvider !== undefined) {
        if (runtime.verbose) console.debug(`${LOG} [${this.name}] Found provider for "${key}" in current scope.`);
        let instance: Providable;
        try {
          if (isDependentProvider(valueProvider)) {
            const deps = {} as Record<ProviderKey, any>;
            for (const dep of valueProvider.deps) {
              // Recursive call continues the chain.
              deps[dep] = this._internalResolve(dep);
            }
            instance = valueProvider.createValue(deps, this);
          } else if (isConstantProvider(valueProvider)) {
            instance = valueProvider.value;
          } else if (typeof valueProvider === 'function') {
            instance = valueProvider(this);
          } else {
            throw new ProviderError(`Unknown provider type for "${key}".`);
          }
        } catch (err) {
          if (err instanceof DIError) throw err;
          throw new ProviderError(`Error resolving provider "${key}".`, { cause: err });
        }
        this.instances.set(key, instance);
        return instance;
      }

      // 4. If no local provider, delegate to parents.
      if (this.parents.length > 0) {
        for (const parent of this.parents) {
          try {
            // The parent continues the *same* resolution chain.
            const instance = parent._internalResolve(key);
            this.instances.set(key, instance);
            return instance;
          } catch (err) {
            if (err instanceof NoProviderError) {
              continue; // This parent didn't have it, try the next one.
            }
            // A different error occurred (e.g., cycle in parent), so propagate it.
            throw new ProviderError(`Error resolving provider "${key}" from parent "${parent.name}"`, { cause: err });
          }
        }
      }
      throw new NoProviderError(`No provider found for "${key}".`);
    } finally {
      // CRITICAL: Pop the current key from the chain as we backtrack up the call stack.
      this.#resolutionChain.pop();
    }
  }
}

/**
 * @internal
 * Type guard to check if a provider is a {DependentProvider}.
 */
export function isDependentProvider<const T extends Providable>(value: unknown): value is DependentProvider<T> {
  return value instanceof DependentProvider;
}

export function isConstantProvider<const T extends Providable>(value: unknown): value is ConstantProvider<T> {
  return value instanceof ConstantProvider;
}

export function isContainer<const Providing extends ProvidedValues>(value: unknown): value is Container<Providing> {
  return value instanceof Container;
}

/**
 * The primary entry point for creating a new dependency container.
 * @param name A descriptive name for the container.
 * @returns A new, empty container instance.
 */
export function createContainer(name: string): Container<{}> {
  if (runtime.verbose) console.debug(`${LOG} Creating new container "${name}"`);
  const container = new Container<{}>(name, {}, new Map());
  return container;
}

export class DIError extends Error {
  constructor(message: string, { cause, debugData }: { cause?: unknown; debugData?: DebugData | undefined } = {}) {
    super(`${message}${debugData ? `\n${stringify(debugData)}` : ''}`);
    this.name = 'DIError';
    this.cause = cause;
  }
}

export class ResolutionError extends DIError {
  constructor(message: string, { cause, debugData }: { cause?: unknown; debugData?: DebugData | undefined } = {}) {
    super(message, { cause, debugData });
    this.name = 'ResolutionError';
  }
}

export class NoProviderError extends DIError {
  constructor(message: string, { cause, debugData }: { cause?: unknown; debugData?: DebugData | undefined } = {}) {
    super(message, { cause, debugData });
    this.name = 'NoProviderError';
  }
}

export class CircularDependencyError extends ResolutionError {
  constructor(message: string, { cause, debugData }: { cause?: unknown; debugData?: DebugData | undefined } = {}) {
    super(message, { cause, debugData });
    this.name = 'CircularDependencyError';
  }
}

export class ProviderError extends DIError {
  constructor(message: string, { cause, debugData }: { cause?: unknown; debugData?: DebugData | undefined } = {}) {
    super(message, { cause, debugData });
    this.name = 'ProviderError';
  }
}
export let debugLazy = false;

export function lazy<T>(fn: () => T, caller = getCaller()): () => T {
  let isInitialized = false;
  let value: T;
  return () => {
    if (!isInitialized) {
      try {
        isInitialized = true;
        if (debugLazy && caller) console.debug(`Initializing lazy value for ${caller}`);
        value = fn();
      } catch (cause) {
        const error = new LazyError(`Unrecoverable error: initializing value for ${caller} failed`, { cause });
        (error as { fatal?: boolean })['fatal'] = true;
        process.exitCode = 66; // shit's bad
        throw error;
      }
    }
    return value;
  };
}

const lazyInScopeMap = new Map<string, unknown>();

/** Remembers the evaluated value associated with the scope, and returns it even when lazyInScope is called again within the same scope. */
export function remember<T>(
  fn: () => T,
  { caller = new Error(), debug = remember.debug }: { caller?: Error | string; debug?: boolean } = {},
): T {
  let callerFrame: ErrorStackParser.StackFrame | undefined;
  let callerName: string;
  let memoKey: string;
  if (typeof caller === 'string') {
    callerName = caller;
    memoKey = caller;
  } else {
    const stack = ErrorStackParser.parse(caller).slice(1);
    callerFrame = stack[0];
    assert(
      stack.length > 0 && callerFrame && callerFrame.functionName && callerFrame.fileName,
      `Can't use lazyInScope if can't identify the caller scope, provide caller explicitly (caller stack: ${caller.stack})`,
    );
    callerName = `${callerFrame.functionName}`.replace(/\s*\[as .*?]\s*/, '');
    const callerHash = quickHash(callerFrame);
    memoKey = `[${callerName}]:${basename(callerFrame.fileName)}:${callerHash}`;
  }
  if (lazyInScopeMap.has(memoKey)) {
    if (debug) console.log(`remember: returning memoized value for ${memoKey}`);
    return lazyInScopeMap.get(memoKey) as T;
  } else {
    try {
      if (debug) console.log(`remember: evaluating value for ${memoKey}`);
      const value = fn();
      if (debug) console.log(`remember: finished evaluation for ${memoKey}`);
      lazyInScopeMap.set(memoKey, value);
      return value;
    } catch (cause) {
      if (debug) console.error(`remember: error during evaluation for ${memoKey}: ${errorMessage(cause)}`, cause);
      throw new LazyError(`Initialization failed for ***${callerName}***: ${errorMessage(cause)}`, {
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        ...callerFrame,
        cause,
        memoKey,
      });
    }
  }
}

remember.debug = false;

export class LazyError extends FatalError {
  constructor(message: string, cause: unknown) {
    super(message, { cause });
    this.name = 'LazyError';
  }
}

function basename(path: string): string {
  return path.split('/').pop() ?? path;
}
